// src/calendar/store.ts
// Web port of app calendarStore behavior: repeat rules, spanDays, exceptions, occurrences.
// Key is the same as app: "calendar_entries_v1".:contentReference[oaicite:5]{index=5}

export type CalendarTag =
  // Existing web proposal markers (keep; mutually exclusive)
  | "date_open"
  | "date_closed"
  // App markers
  | "unavailable"
  | "period"
  | "sick"
  | "date_night"
  | "flirty"
  | "passionate"
  | "intimate";

export type RepeatRule = "none" | "weekly" | "monthly"; // app-compatible:contentReference[oaicite:6]{index=6}

export type CalendarEntry = {
  id: string;
  date: string; // YYYY-MM-DD
  tag: CalendarTag;
  note?: string;
  repeat?: RepeatRule;
  spanDays?: number;
  exceptions?: string[]; // dates to skip for repeating entries (YYYY-MM-DD)
  createdAt: number; // epoch ms
};

export type Occurrence = { date: string; entry: CalendarEntry };

const KEY = "calendar_entries_v1";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseISO(iso: string): Date {
  // iso is YYYY-MM-DD; treat as local date
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function toISO(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysISO(iso: string, days: number) {
  const d = parseISO(iso);
  d.setDate(d.getDate() + days);
  return toISO(d);
}

function addMonthsISO(iso: string, months: number) {
  const d = parseISO(iso);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return toISO(d);
}

function uid(): string {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function safeParse<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

let mem: CalendarEntry[] | null = null;

export function loadCalendarEntries(): CalendarEntry[] {
  if (mem) return mem;
  const raw = typeof window !== "undefined" ? window.localStorage.getItem(KEY) : null;
  mem = safeParse<CalendarEntry[]>(raw, []);
  return mem;
}

export function saveCalendarEntries(entries: CalendarEntry[]) {
  mem = entries;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // ignore
  }
}

export function addCalendarEntry(partial: Omit<CalendarEntry, "id" | "createdAt">) {
  const next: CalendarEntry = {
    id: uid(),
    createdAt: Date.now(),
    repeat: "none",
    spanDays: 1,
    ...partial,
    exceptions: partial.exceptions ?? [],
  };
  const entries = [...loadCalendarEntries(), next];
  saveCalendarEntries(entries);
  return next;
}

export function removeCalendarEntry(id: string) {
  const entries = loadCalendarEntries().filter((e) => e.id !== id);
  saveCalendarEntries(entries);
}

// Prefer excluding one occurrence if entry repeats (matches intent in app store).:contentReference[oaicite:7]{index=7}
export function excludeOccurrence(entryId: string, dateISO: string) {
  const entries = loadCalendarEntries().map((e) => {
    if (e.id !== entryId) return e;
    const ex = new Set([...(e.exceptions || []), dateISO]);
    return { ...e, exceptions: Array.from(ex) };
  });
  saveCalendarEntries(entries);
}

function expandEntry(entry: CalendarEntry, fromISO: string, toISO: string): Occurrence[] {
  const span = Math.max(1, entry.spanDays ?? 1);
  const out: Occurrence[] = [];
  const exceptions = new Set((entry.exceptions || []).map(String));

  const bases: string[] = [entry.date];

  if (entry.repeat === "weekly") {
    let i = 1;
    while (true) {
      const cur = addDaysISO(entry.date, i * 7);
      if (cur > toISO) break;
      bases.push(cur);
      i++;
      if (bases.length > 260) break;
    }
  } else if (entry.repeat === "monthly") {
    let i = 1;
    while (true) {
      const cur = addMonthsISO(entry.date, i);
      if (cur > toISO) break;
      bases.push(cur);
      i++;
      if (bases.length > 260) break;
    }
  }

  for (const base of bases) {
    for (let i = 0; i < span; i++) {
      const d = addDaysISO(base, i);
      if (d < fromISO || d > toISO) continue;
      if (exceptions.has(d)) continue;
      out.push({ date: d, entry });
    }
  }

  return out;
}

export function getOccurrencesInRange(fromISO: string, toISO: string) {
  const entries = loadCalendarEntries();
  const out: Occurrence[] = [];
  for (const e of entries) out.push(...expandEntry(e, fromISO, toISO));
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return out;
}

export function getOccurrencesForDate(dateISO: string) {
  return getOccurrencesInRange(dateISO, dateISO);
}
